import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { Store } from '../../Store';
import { api } from '../../Api';
import { getError } from '../../utils';

const CHANNEL_META = {
  whatsapp: { label: 'WhatsApp', icon: 'fa-whatsapp', color: '#16a34a', soft: '#dcfce7' },
  sms: { label: 'SMS', icon: 'fa-commenting', color: '#2563eb', soft: '#dbeafe' },
};

const GROUP_ICON = {
  Withdraw: 'fa-money',
  'Merchant withdraw': 'fa-handshake-o',
  Convert: 'fa-exchange',
  Purchase: 'fa-shopping-bag',
  Topup: 'fa-plus-circle',
};

// Same substitution the server does, so the preview is exact.
const fill = (template, vars) =>
  String(template || '').replace(/\{(\w+)\}/g, (m, t) =>
    vars[t] === undefined || vars[t] === null ? m : String(vars[t])
  );

// SMS segments: GSM-7 fits 160 per part (153 when split); anything outside it
// (Bangla, emoji, ৳) drops to UCS-2 at 70/67. Shown so the admin sees cost.
const smsParts = (text) => {
  const t = String(text || '');
  if (!t) return { parts: 0, unicode: false };
  // eslint-disable-next-line no-control-regex
  const unicode = /[^\x00-\x7F]/.test(t);
  const single = unicode ? 70 : 160;
  const multi = unicode ? 67 : 153;
  return { parts: t.length <= single ? 1 : Math.ceil(t.length / multi), unicode };
};

export default function MessageCenterScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const auth = useMemo(
    () => ({ headers: { authorization: `Bearer ${userInfo.token}` } }),
    [userInfo.token]
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [sample, setSample] = useState({});
  const [gateways, setGateways] = useState({ sms: 0, whatsapp: 0 });
  const [events, setEvents] = useState({});
  const [saved, setSaved] = useState('');
  const [open, setOpen] = useState('');
  const [group, setGroup] = useState('All');
  const [query, setQuery] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState('');
  const textRefs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await Axios.get(api + '/api/notifications/admin', auth);
      setCatalog(data.catalog || []);
      setSample(data.sampleVars || {});
      setGateways(data.gateways || { sms: 0, whatsapp: 0 });
      setEvents(data.events || {});
      setSaved(JSON.stringify(data.events || {}));
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = saved !== JSON.stringify(events);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await Axios.put(api + '/api/notifications/admin', { events }, auth);
      setEvents(data.events);
      setSaved(JSON.stringify(data.events));
      toast.success(data.message || 'Saved.');
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSaving(false);
    }
  };

  const patch = (key, fn) =>
    setEvents((cur) => {
      const next = structuredClone(cur);
      fn(next[key]);
      return next;
    });

  const move = (key, idx, dir) =>
    patch(key, (ev) => {
      const j = idx + dir;
      if (j < 0 || j >= ev.order.length) return;
      [ev.order[idx], ev.order[j]] = [ev.order[j], ev.order[idx]];
    });

  const insertVar = (key, channel, v) => {
    const el = textRefs.current[`${key}-${channel}`];
    const token = `{${v}}`;
    patch(key, (ev) => {
      const t = ev.templates[channel] || '';
      if (el && typeof el.selectionStart === 'number') {
        ev.templates[channel] = t.slice(0, el.selectionStart) + token + t.slice(el.selectionEnd);
      } else {
        ev.templates[channel] = t + token;
      }
    });
    setTimeout(() => el && el.focus(), 0);
  };

  const sendTest = async (key, channel) => {
    if (!testPhone.trim()) {
      toast.error('Enter a test phone number at the top first.');
      return;
    }
    if (dirty) toast.info('Testing the SAVED text. Save first to test your edits.');
    setTesting(`${key}-${channel}`);
    try {
      const { data } = await Axios.post(
        api + '/api/notifications/admin/test',
        { event: key, channel, phone: testPhone.trim() },
        auth
      );
      toast.success(data.message);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setTesting('');
    }
  };

  const groups = useMemo(() => ['All', ...new Set(catalog.map((c) => c.group))], [catalog]);
  const visible = catalog.filter(
    (c) =>
      (group === 'All' || c.group === group) &&
      (!query.trim() || `${c.label} ${c.group}`.toLowerCase().includes(query.trim().toLowerCase()))
  );
  const byGroup = visible.reduce((acc, c) => {
    (acc[c.group] = acc[c.group] || []).push(c);
    return acc;
  }, {});

  const onCount = Object.values(events).filter((e) => e.enabled).length;
  const waFirst = Object.values(events).filter((e) => e.enabled && e.order[0] === 'whatsapp' && e.channels.whatsapp).length;

  return (
    <div className="mc-page">
      <Helmet>
        <title>Message Center</title>
      </Helmet>
      <style>{CSS}</style>

      <div className="mc-head">
        <div>
          <h1>Message Center</h1>
          <p>
            WhatsApp and SMS messages members and merchants get for withdraw, convert, purchase and
            topup. Channels are tried in order; the next one is used only if the first could not
            send.
          </p>
        </div>
        <button className="mc-save" onClick={save} disabled={!dirty || saving}>
          {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
        </button>
      </div>

      <div className="mc-tiles">
        <div className="mc-tile">
          <small>Messages on</small>
          <strong>
            {onCount}
            <span> / {catalog.length}</span>
          </strong>
        </div>
        <div className="mc-tile">
          <small>WhatsApp first</small>
          <strong>{waFirst}</strong>
        </div>
        <div className={`mc-tile ${gateways.whatsapp ? '' : 'mc-warn'}`}>
          <small>
            <i className="fa fa-whatsapp" /> WhatsApp gateways on
          </small>
          <strong>{gateways.whatsapp}</strong>
        </div>
        <div className={`mc-tile ${gateways.sms ? '' : 'mc-warn'}`}>
          <small>
            <i className="fa fa-commenting" /> SMS gateways on
          </small>
          <strong>{gateways.sms}</strong>
        </div>
      </div>

      {(!gateways.whatsapp || !gateways.sms) && !loading ? (
        <div className="mc-alert">
          <i className="fa fa-exclamation-triangle" />{' '}
          {!gateways.whatsapp && 'No WhatsApp gateway is switched on, so WhatsApp steps are skipped and SMS picks up. '}
          {!gateways.sms && 'No SMS gateway is switched on. '}
          Set them up in <Link to="/julkarnine">Developers → BeSends Gateways</Link>.
        </div>
      ) : null}

      <div className="mc-toolbar">
        <div className="mc-chips">
          {groups.map((g) => (
            <button key={g} className={g === group ? 'mc-chip on' : 'mc-chip'} onClick={() => setGroup(g)}>
              {g !== 'All' && <i className={`fa ${GROUP_ICON[g] || 'fa-bell'}`} />} {g}
            </button>
          ))}
        </div>
        <input
          className="mc-input"
          placeholder="Search messages…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <input
          className="mc-input"
          placeholder="Test phone e.g. 01712345678"
          value={testPhone}
          onChange={(e) => setTestPhone(e.target.value)}
        />
      </div>

      {loading ? <div className="mc-empty">Loading…</div> : null}

      {Object.entries(byGroup).map(([g, items]) => (
        <section key={g} className="mc-group">
          <h2>
            <i className={`fa ${GROUP_ICON[g] || 'fa-bell'}`} /> {g}
          </h2>
          {items.map((meta) => {
            const ev = events[meta.key];
            if (!ev) return null;
            const isOpen = open === meta.key;
            const route = ev.order.filter((c) => ev.channels[c]);
            return (
              <article key={meta.key} className={`mc-card ${ev.enabled ? '' : 'is-off'}`}>
                <header className="mc-card-head" onClick={() => setOpen(isOpen ? '' : meta.key)}>
                  <div className="mc-title">
                    <b>{meta.label}</b>
                    <span className="mc-to">to {meta.recipient}</span>
                  </div>
                  <div className="mc-route">
                    {route.length ? (
                      route.map((c, i) => (
                        <React.Fragment key={c}>
                          {i > 0 && <i className="fa fa-long-arrow-right mc-arrow" />}
                          <span className="mc-pill" style={{ background: CHANNEL_META[c].soft, color: CHANNEL_META[c].color }}>
                            <i className={`fa ${CHANNEL_META[c].icon}`} /> {CHANNEL_META[c].label}
                          </span>
                        </React.Fragment>
                      ))
                    ) : (
                      <span className="mc-muted">no channel</span>
                    )}
                  </div>
                  <label className="mc-switch" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={ev.enabled}
                      onChange={(e) => patch(meta.key, (x) => { x.enabled = e.target.checked; })}
                    />
                    <span />
                  </label>
                  <i className={`fa fa-chevron-${isOpen ? 'up' : 'down'} mc-chev`} />
                </header>

                {isOpen && (
                  <div className="mc-body">
                    <div className="mc-order">
                      <div className="mc-label">Channel order</div>
                      {ev.order.map((c, idx) => (
                        <div key={c} className="mc-order-row">
                          <span className="mc-rank">{idx + 1}</span>
                          <span className="mc-pill" style={{ background: CHANNEL_META[c].soft, color: CHANNEL_META[c].color }}>
                            <i className={`fa ${CHANNEL_META[c].icon}`} /> {CHANNEL_META[c].label}
                          </span>
                          <span className="mc-muted mc-grow">
                            {idx === 0 ? 'Tried first' : 'Only if the one above could not send'}
                          </span>
                          <button className="mc-icon" disabled={idx === 0} onClick={() => move(meta.key, idx, -1)} title="Earlier">
                            <i className="fa fa-arrow-up" />
                          </button>
                          <button className="mc-icon" disabled={idx === ev.order.length - 1} onClick={() => move(meta.key, idx, 1)} title="Later">
                            <i className="fa fa-arrow-down" />
                          </button>
                          <label className="mc-switch sm">
                            <input
                              type="checkbox"
                              checked={ev.channels[c]}
                              onChange={(e) => patch(meta.key, (x) => { x.channels[c] = e.target.checked; })}
                            />
                            <span />
                          </label>
                        </div>
                      ))}
                      <label className="mc-check">
                        <input
                          type="checkbox"
                          checked={ev.inApp}
                          onChange={(e) => patch(meta.key, (x) => { x.inApp = e.target.checked; })}
                        />{' '}
                        Also add an in-app notification (free)
                      </label>
                    </div>

                    <div className="mc-templates">
                      {ev.order.map((c) => {
                        const text = ev.templates[c] || '';
                        const preview = fill(text, sample);
                        const seg = smsParts(preview);
                        return (
                          <div key={c} className={`mc-tpl ${ev.channels[c] ? '' : 'is-off'}`}>
                            <div className="mc-tpl-head">
                              <span style={{ color: CHANNEL_META[c].color }}>
                                <i className={`fa ${CHANNEL_META[c].icon}`} /> {CHANNEL_META[c].label} text
                              </span>
                              <span className="mc-muted">
                                {text.length} chars
                                {c === 'sms' && seg.parts ? ` · ${seg.parts} SMS${seg.unicode ? ' (unicode)' : ''}` : ''}
                              </span>
                            </div>
                            <textarea
                              ref={(el) => { textRefs.current[`${meta.key}-${c}`] = el; }}
                              rows={3}
                              value={text}
                              onChange={(e) => patch(meta.key, (x) => { x.templates[c] = e.target.value; })}
                            />
                            <div className="mc-vars">
                              {(meta.vars || []).map((v) => (
                                <button key={v} onClick={() => insertVar(meta.key, c, v)}>{`{${v}}`}</button>
                              ))}
                              <button
                                className="mc-reset"
                                onClick={() => patch(meta.key, (x) => { x.templates[c] = meta.defaultTemplate || ''; })}
                              >
                                Reset
                              </button>
                            </div>
                            <div className="mc-preview">
                              <small>Preview</small>
                              <p>{preview || <span className="mc-muted">Empty — this channel will be skipped.</span>}</p>
                            </div>
                            <button
                              className="mc-test"
                              onClick={() => sendTest(meta.key, c)}
                              disabled={testing === `${meta.key}-${c}`}
                            >
                              {testing === `${meta.key}-${c}` ? 'Sending…' : `Send test ${CHANNEL_META[c].label} (costs credit)`}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </section>
      ))}

      {dirty && (
        <div className="mc-sticky">
          <span>You have unsaved changes.</span>
          <button className="mc-ghost" onClick={() => setEvents(JSON.parse(saved))}>Discard</button>
          <button className="mc-save" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      )}
    </div>
  );
}

const CSS = `
.mc-page { max-width: 1100px; margin: 0 auto; padding: 16px 16px 96px; color: #0f1b2d; }
.mc-head { display: flex; gap: 16px; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
.mc-head h1 { font-size: 1.5rem; font-weight: 700; margin: 0 0 4px; }
.mc-head p { color: #6b7a90; margin: 0; max-width: 680px; font-size: .9rem; }
.mc-save { border: 0; background: #0f7a55; color: #fff; font-weight: 600; padding: 10px 18px; border-radius: 10px; white-space: nowrap; }
.mc-save:disabled { background: #9fb8ad; }
.mc-ghost { border: 1px solid #d5dce6; background: #fff; padding: 10px 14px; border-radius: 10px; }
.mc-tiles { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 12px; margin-bottom: 14px; }
.mc-tile { background: #fff; border: 1px solid #e6ebf2; border-radius: 14px; padding: 12px 14px; }
.mc-tile small { color: #6b7a90; display: block; font-size: .75rem; }
.mc-tile strong { font-size: 1.4rem; }
.mc-tile strong span { font-size: .9rem; color: #6b7a90; font-weight: 500; }
.mc-tile.mc-warn { border-color: #fcd34d; background: #fffbeb; }
.mc-alert { background: #fffbeb; border: 1px solid #fcd34d; color: #92400e; border-radius: 12px; padding: 10px 14px; margin-bottom: 14px; font-size: .88rem; }
.mc-toolbar { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; margin-bottom: 16px; }
.mc-chips { display: flex; gap: 6px; flex-wrap: wrap; flex: 1 1 100%; }
.mc-chip { border: 1px solid #d5dce6; background: #fff; border-radius: 999px; padding: 6px 12px; font-size: .82rem; }
.mc-chip.on { background: #0f1b2d; color: #fff; border-color: #0f1b2d; }
.mc-input { flex: 1 1 220px; border: 1px solid #d5dce6; border-radius: 10px; padding: 9px 12px; min-width: 0; }
.mc-group h2 { font-size: .8rem; text-transform: uppercase; letter-spacing: .08em; color: #6b7a90; margin: 18px 0 8px; }
.mc-card { background: #fff; border: 1px solid #e6ebf2; border-radius: 14px; margin-bottom: 10px; overflow: hidden; }
.mc-card.is-off { opacity: .65; }
.mc-card-head { display: flex; align-items: center; gap: 12px; padding: 12px 14px; cursor: pointer; }
.mc-title { flex: 1; min-width: 0; }
.mc-title b { display: block; font-size: .95rem; }
.mc-to { font-size: .75rem; color: #6b7a90; }
.mc-route { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.mc-pill { display: inline-flex; gap: 5px; align-items: center; padding: 3px 9px; border-radius: 999px; font-size: .75rem; font-weight: 600; }
.mc-arrow { color: #9aa6b8; font-size: .8rem; }
.mc-muted { color: #8a97aa; font-size: .8rem; }
.mc-chev { color: #9aa6b8; }
.mc-switch { position: relative; width: 42px; height: 24px; flex: none; margin: 0; }
.mc-switch.sm { width: 36px; height: 20px; }
.mc-switch input { opacity: 0; width: 0; height: 0; }
.mc-switch span { position: absolute; inset: 0; background: #cbd5e1; border-radius: 999px; transition: .2s; cursor: pointer; }
.mc-switch span::before { content: ''; position: absolute; width: calc(100% / 2 - 4px); aspect-ratio: 1; left: 3px; top: 50%; transform: translateY(-50%); background: #fff; border-radius: 50%; transition: .2s; }
.mc-switch input:checked + span { background: #0f7a55; }
.mc-switch input:checked + span::before { left: calc(50% + 1px); }
.mc-body { border-top: 1px solid #eef2f7; padding: 14px; display: grid; grid-template-columns: 300px minmax(0,1fr); gap: 16px; }
.mc-label { font-size: .75rem; text-transform: uppercase; letter-spacing: .06em; color: #6b7a90; margin-bottom: 8px; }
.mc-order-row { display: flex; align-items: center; gap: 8px; padding: 8px; border: 1px solid #eef2f7; border-radius: 10px; margin-bottom: 8px; }
.mc-rank { width: 22px; height: 22px; border-radius: 50%; background: #0f1b2d; color: #fff; font-size: .72rem; display: grid; place-items: center; flex: none; }
.mc-grow { flex: 1; min-width: 0; }
.mc-icon { border: 1px solid #e2e8f0; background: #fff; border-radius: 8px; width: 28px; height: 28px; flex: none; }
.mc-check { font-size: .85rem; margin-top: 6px; }
.mc-templates { display: grid; gap: 12px; }
.mc-tpl { border: 1px solid #eef2f7; border-radius: 12px; padding: 12px; }
.mc-tpl.is-off { opacity: .55; }
.mc-tpl-head { display: flex; justify-content: space-between; font-weight: 600; font-size: .85rem; margin-bottom: 6px; }
.mc-tpl textarea { width: 100%; border: 1px solid #d5dce6; border-radius: 10px; padding: 8px 10px; font-size: .9rem; resize: vertical; }
.mc-vars { display: flex; flex-wrap: wrap; gap: 5px; margin: 6px 0; }
.mc-vars button { border: 1px dashed #b8c4d6; background: #f8fafc; border-radius: 6px; font-size: .72rem; padding: 2px 7px; font-family: 'Roboto Mono', monospace; }
.mc-vars .mc-reset { border-style: solid; font-family: inherit; margin-left: auto; }
.mc-preview { background: #f8fafc; border-radius: 10px; padding: 8px 10px; }
.mc-preview small { color: #6b7a90; font-size: .7rem; text-transform: uppercase; letter-spacing: .06em; }
.mc-preview p { margin: 2px 0 0; font-size: .88rem; white-space: pre-wrap; }
.mc-test { margin-top: 8px; border: 1px solid #d5dce6; background: #fff; border-radius: 8px; padding: 6px 10px; font-size: .8rem; }
.mc-empty { text-align: center; color: #6b7a90; padding: 30px; }
.mc-sticky { position: fixed; left: 50%; transform: translateX(-50%); bottom: 16px; background: #0f1b2d; color: #fff; border-radius: 14px; padding: 10px 12px 10px 16px; display: flex; gap: 10px; align-items: center; box-shadow: 0 12px 30px rgba(0,0,0,.25); z-index: 50; width: min(560px, calc(100% - 32px)); }
.mc-sticky span { flex: 1; font-size: .88rem; }
@media (max-width: 800px) {
  .mc-tiles { grid-template-columns: repeat(2, minmax(0,1fr)); }
  .mc-body { grid-template-columns: 1fr; }
  .mc-head { flex-direction: column; }
}
@media (max-width: 480px) {
  .mc-card-head { flex-wrap: wrap; }
  .mc-route { order: 3; width: 100%; }
}
`;
